import dayjs, { Dayjs, ManipulateType } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'

import { Aggregation } from '@/types/impact'

dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(isBetween)
dayjs.extend(weekOfYear)
dayjs.extend(weekday)
dayjs.extend(localeData)

export default dayjs

const HOUR_SECS = 3600
const DAY_MS = 86400000
const WEEK_MS = 604800000

export { DAY_MS, HOUR_SECS, WEEK_MS }

// INTL_FORMATS are for internatiolization of dates for users.
export const INTL_FORMATS = {
  HOURS: '{hours}',
  HOURS_MINUTES: '{hours} {minutes}',
  MINUTES: '{minutes}',
}
// DATE_FORMATS are for parsing or formatting of dates for APIs.
export const DATE_FORMATS = {
  ISO_DATE: 'YYYY-MM-DD',
  MONTH_FULL: 'MMMM',
  MONTH_SHORT: 'MMM',
  YEAR_FULL: 'YYYY',
  YEAR_SHORT: 'YY',
}

export function parseApiDateTimeTimezone(dateTime: string): Date {
  return dayjs(dateTime).toDate()
}

export function diffDays(date1: Date, date2: Date): number {
  const utc1 = dayjs(date1)
  const utc2 = dayjs(date2)
  const result = Math.abs(utc1.diff(utc2, 'day'))

  // if the difference is less than 24 hours
  // but the two dates are different days we want to return 1
  if (result === 0 && utc1.date() !== utc2.date()) {
    return 1
  }
  return result
}

export function diffWeeks(date1: string, date2: string): number {
  const utc1 = dayjs.utc(date1)
  const utc2 = dayjs.utc(date2)
  return utc2.diff(utc1, 'week')
}

export function diffMonths(date1: Date, date2: Date): number {
  const utc1 = dayjs.utc(date1)
  const utc2 = dayjs.utc(date2)
  return utc2.diff(utc1, 'month')
}

export function nextFirstOfMonth(): Date {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = 1
  return new Date(year, month + 1, day)
}

export function getMonthlyDateRange(
  amount = 3,
  rangeEndDate = undefined,
  isLastMonthIncluded = undefined
): { from: string; to: string } {
  const rangeToDate =
    isLastMonthIncluded !== undefined
      ? dayjs.utc(rangeEndDate).endOf('month').add(-1, 'months')
      : dayjs.utc(rangeEndDate)

  const rangeFromDate =
    isLastMonthIncluded !== undefined
      ? dayjs.utc(rangeToDate).add(-2, 'months').startOf('month').toString()
      : undefined

  return getDateRange({
    amount,
    rangeEndDate: rangeToDate.toString(),
    unit: 'months',
    rangeFromDate,
  })
}

export function getDateRange({
  amount,
  rangeEndDate,
  unit,
  rangeFromDate,
}: {
  amount: number
  rangeEndDate: string
  unit: 'hours' | 'days' | 'months'
  rangeFromDate?: string
}): { from: string; to: string } {
  const from = rangeFromDate
    ? dayjs.utc(rangeFromDate)
    : dayjs.utc(rangeEndDate).add(-1 * amount, unit)
  const to = dayjs.utc(rangeEndDate)

  return {
    from: from?.isValid() ? from.format(DATE_FORMATS.ISO_DATE) : null,
    to: to?.isValid() ? to.format(DATE_FORMATS.ISO_DATE) : null,
  }
}

export function formatDateToIsoDate(date: Date) {
  return dayjs(date).utc().format(DATE_FORMATS.ISO_DATE)
}

export function getDateRangeChangeHandler({
  onDateRangeChange,
  onTemporalAggregationChange = undefined,
}: {
  onDateRangeChange: (value: [Dayjs | string, Dayjs | string]) => void
  onTemporalAggregationChange?: (value: string) => void
}) {
  return (dateRanges: [Dayjs, Dayjs] | undefined) => {
    if (dateRanges && dateRanges.length === 2) {
      onDateRangeChange([
        dayjs.utc(dateRanges[0]).local().format(DATE_FORMATS.ISO_DATE),
        dayjs.utc(dateRanges[1]).local().format(DATE_FORMATS.ISO_DATE),
      ])
      onTemporalAggregationChange &&
        getAggregation(dateRanges, onTemporalAggregationChange)
    }
  }
}

export function getAggregation(dateRanges, onTemporalAggregationChange) {
  const [from, to] = dateRanges
  const monthsSelected = dayjs(to).diff(from, 'month') + 1
  monthsSelected >= 3
    ? onTemporalAggregationChange(Aggregation.MONTHLY)
    : onTemporalAggregationChange(Aggregation.WEEKLY)
}

export function isDateBeforeThan(
  date: Dayjs,
  amount: number,
  unit: ManipulateType
): boolean {
  const limitDate = dayjs.utc().subtract(amount, unit)
  return date <= limitDate
}

export function isDateInFuture(date: Dayjs): boolean {
  return dayjs.utc(dayjs()).isBefore(date)
}

export function expandDateRangeToWholeMonth(
  datesRange: [Dayjs, Dayjs]
): [Dayjs, Dayjs] {
  const [from, to] = datesRange
  let expandedTo =
    to === null
      ? dayjs().subtract(1, 'months').endOf('month')
      : to.endOf('month')

  if (isDateInFuture(expandedTo)) {
    expandedTo = dayjs().subtract(1, 'days').utc()
  }

  const expandedFrom =
    from === null
      ? expandedTo.subtract(3, 'months').startOf('month')
      : from.startOf('month')
  return [expandedFrom, expandedTo]
}

export function getMonthsDifference({
  dateRangeDates,
  includesWholeMonth,
}: {
  dateRangeDates: Dayjs[]
  includesWholeMonth?: boolean
}) {
  const [from, to] = dateRangeDates
  return includesWholeMonth
    ? dayjs(to).diff(from, 'month') + 1
    : dayjs(to).diff(from, 'month')
}

export function getTimeToMs(h: number, m: number) {
  const hours = Math.floor(h * 3600000)
  const minutes = Math.floor(m * 60000)

  return { milliseconds: hours + minutes }
}

export function formatLineChartAxisDatesPerMonth(data, from) {
  return data === from.format('YYYY-MM-DD')
    ? dayjs(data).format('MMM YY')
    : dayjs(data).format('MMM')
}

export function formatLineChartAxisDatesPerDay(data) {
  return dayjs(data).format('DD MMM')
}

export function formatLineChartTooltipDates(data) {
  return dayjs(data).format('MMMM YYYY')
}

export function getRangeDateFormat(date, options) {
  const dateFormatParts = []
  const [from, to] = date

  const dateParts = Intl.DateTimeFormat(
    navigator.language,
    options
  ).formatRangeToParts(from?.toDate(), to?.toDate())

  for (const part of dateParts) {
    switch (part.type) {
      case 'day':
        part.source === 'startRange' && dateFormatParts.push('DD')
        break
      case 'month':
        part.source === 'startRange' && dateFormatParts.push('MM')
        break
      case 'year':
        part.source === 'startRange' && dateFormatParts.push('YYYY')
        break
      case 'literal':
        part.source === 'startRange' && dateFormatParts.push(part.value)
    }
  }
  return dateFormatParts.join('')
}

export function getBasicDateFormat(date) {
  const dateFormatParts = []
  const dateParts = Intl.DateTimeFormat(navigator.language).formatToParts(date)

  for (const part of dateParts) {
    switch (part.type) {
      case 'day':
        dateFormatParts.push('DD')
        break
      case 'month':
        dateFormatParts.push('MM')
        break
      case 'year':
        dateFormatParts.push('YYYY')
        break
      case 'literal':
        dateFormatParts.push(part.value)
    }
  }
  return dateFormatParts.join('')
}
